import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { BackButton, Button, Input, Spinner } from '../components/core';
import { createOrg, isSlugAvailable, isValidSlug } from '../services/org.service';
import { COUNTRIES } from '../constants.ts';

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export default function CreateOrgPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  function slugHint(status: SlugStatus, slug: string): { text: string; color: string } | null {
    if (!slug) return null;
    if (status === 'invalid') return { text: t('createOrg.slugInvalid'), color: 'text-red-600' };
    if (status === 'checking') return null;
    if (status === 'taken') return { text: t('createOrg.slugTaken'), color: 'text-red-600' };
    if (status === 'available') return { text: t('createOrg.slugAvailable', { slug }), color: 'text-green-700' };
    return { text: t('createOrg.slugHint', { slug }), color: 'text-gray-500' };
  }

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [country, setCountry] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setSlugStatus('idle'); return; }
    if (!isValidSlug(slug)) { setSlugStatus('invalid'); return; }

    setSlugStatus('checking');
    const timer = setTimeout(() => {
      isSlugAvailable(slug)
        .then((available) => setSlugStatus(available ? 'available' : 'taken'))
        .catch((error) => {
          console.error('Slug availability check failed:', error);
          setSlugStatus('idle');
        });
    }, 400);

    return () => clearTimeout(timer);
  }, [slug]);

  const canSubmit =
    name.trim() &&
    slugStatus === 'available' &&
    country &&
    contactName.trim() &&
    contactPhone.trim() &&
    contactEmail.trim() &&
    !isSubmitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const orgSlug = await createOrg({
        name: name.trim(),
        slug,
        country,
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim(),
        logoUrl: logoUrl.trim() || undefined,
        creatorUid: user.uid,
        creatorDisplayName: user.displayName ?? '',
        creatorEmail: user.email ?? '',
      });
      navigate(`/org/${encodeURIComponent(orgSlug)}`, { replace: true });
    } catch (error) {
      console.error('Organization creation failed:', error);
      setSubmitError(t('createOrg.errorFailed'));
      setIsSubmitting(false);
    }
  }

  const hint = slugHint(slugStatus, slug);

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => navigate('/')}
        onAvatarClick={() => navigate('/profile')}
      />

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <BackButton />

        <h1 className="mb-1 font-display text-2xl font-semibold text-gray-900">
          {t('createOrg.title')}
        </h1>
        <p className="mb-8 font-body text-sm text-gray-500">
          {t('createOrg.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Organization Details */}
          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="font-body text-sm font-semibold text-gray-700">{t('createOrg.orgDetails')}</h2>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('createOrg.orgName')}
                  required
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Khalil Farms"
                  autoFocus
                />
                <div className="flex flex-col gap-1.5">
                  <Input
                    label={t('createOrg.urlSlug')}
                    required
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="e.g. khalil"
                    className="font-mono"
                    error={slugStatus === 'invalid' || slugStatus === 'taken' ? ' ' : undefined}
                  />
                  {hint && (
                    <p className={`-mt-1 text-xs ${hint.color}`}>{hint.text}</p>
                  )}
                  {slugStatus === 'checking' && (
                    <div className="-mt-1 flex items-center gap-1.5">
                      <Spinner size="sm" />
                      <p className="text-xs text-gray-500">{t('createOrg.slugChecking')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-3xs font-semibold tracking-wider text-gray-700">
                    {t('createOrg.country')} <span className="ms-0.5 text-red-600">*</span>
                  </label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    className="w-full rounded-sm border border-gray-300 bg-white px-3.5 py-2.75 font-body text-base text-gray-900 outline-none transition focus:border-green-600 focus:ring-4 focus:ring-green-600/15"
                  >
                    <option value="">{t('createOrg.selectCountry')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label={t('createOrg.logoUrl')}
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://…"
                  helperText={t('createOrg.logoUrlHelper')}
                />
              </div>
            </div>
          </section>

          {/* Contact Details */}
          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="font-body text-sm font-semibold text-gray-700">{t('createOrg.contactDetails')}</h2>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <Input
                label={t('createOrg.contactName')}
                required
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="e.g. Ahmad Khalil"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('createOrg.contactPhone')}
                  required
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="e.g. 050-123-4567"
                />
                <Input
                  label={t('createOrg.contactEmail')}
                  required
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="e.g. ahmad@example.com"
                />
              </div>
            </div>
          </section>

          {submitError && (
            <p className="text-sm text-red-600">{submitError}</p>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/')}
              disabled={isSubmitting}
            >
              {t('createOrg.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
            >
              {isSubmitting ? t('createOrg.creating') : t('createOrg.create')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
